import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { authAPI } from '../services/api';
import i18n from '../i18n';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Periodic-validation timer lives in a ref, not state, so the effect that
  // owns it doesn't depend on its own output (which caused the effect to
  // re-run on every interval set/clear, blowing up validation reliability).
  const tokenValidationIntervalRef = useRef(null);

  // Helper function to sync language based on user's locked language
  const syncLanguageWithUser = (userData) => {
    if (userData?.lockedLanguage) {
      const currentLang = i18n.language;
      if (currentLang !== userData.lockedLanguage) {
        console.log('Syncing language to locked preference:', userData.lockedLanguage);
        i18n.changeLanguage(userData.lockedLanguage);
        document.documentElement.dir = userData.lockedLanguage === 'he' ? 'rtl' : 'ltr';
        document.documentElement.lang = userData.lockedLanguage;
        localStorage.setItem('eyehill_language', userData.lockedLanguage);
      }
    }
  };

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Simply check if we have stored auth data
        if (authAPI.isAuthenticated()) {
          const currentUser = authAPI.getCurrentUser();
          // Validate token is still valid by making a test API call
          try {
            const validationResponse = await authAPI.validateToken();
            // Use the fresh user data from server, not the stale localStorage data
            const freshUser = validationResponse.user || currentUser;
            
            // Preserve impersonation data if it exists in current user
            if (currentUser?.isImpersonation) {
              freshUser.isImpersonation = currentUser.isImpersonation;
              freshUser.impersonatedBy = currentUser.impersonatedBy;
              freshUser.impersonatedByEmail = currentUser.impersonatedByEmail;
            }
            
            setUser(freshUser);
            // Update localStorage with fresh user data
            localStorage.setItem('user', JSON.stringify(freshUser));
            // Sync language if user has locked language preference
            syncLanguageWithUser(freshUser);
          } catch (validationError) {
            // Token is invalid, clear auth data
            console.log('Token validation failed, clearing auth state');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('onboardingProgress');
            localStorage.removeItem('searchExecutionState');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear potentially corrupted auth data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('onboardingProgress');
        localStorage.removeItem('searchExecutionState');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Set up periodic token validation (every 5 minutes).
  // Depends only on user.id — not on the interval handle itself — so the
  // effect runs at most once per logged-in session.
  useEffect(() => {
    if (!user) return undefined;

    const interval = setInterval(async () => {
      try {
        const validationResponse = await authAPI.validateToken();
        if (validationResponse.user) {
          const freshUser = validationResponse.user;
          // Preserve impersonation data if it exists on the current user
          setUser(prev => {
            if (prev?.isImpersonation) {
              freshUser.isImpersonation = prev.isImpersonation;
              freshUser.impersonatedBy = prev.impersonatedBy;
              freshUser.impersonatedByEmail = prev.impersonatedByEmail;
            }
            localStorage.setItem('user', JSON.stringify(freshUser));
            syncLanguageWithUser(freshUser);
            return freshUser;
          });
        }
      } catch (error) {
        console.log('Token validation failed, logging out');
        clearInterval(interval);
        tokenValidationIntervalRef.current = null;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('onboardingProgress');
        localStorage.removeItem('searchExecutionState');
        setUser(null);
        setError(null);
        window.location.replace('/');
      }
    }, 5 * 60 * 1000);

    tokenValidationIntervalRef.current = interval;

    return () => {
      clearInterval(interval);
      tokenValidationIntervalRef.current = null;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.login(credentials);
      setUser(response.user);
      // Sync language if user has locked language preference
      syncLanguageWithUser(response.user);
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.register(userData);
      setUser(response.user);
      // Sync language if user has locked language preference
      syncLanguageWithUser(response.user);
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    if (tokenValidationIntervalRef.current) {
      clearInterval(tokenValidationIntervalRef.current);
      tokenValidationIntervalRef.current = null;
    }
    // Clear auth data but preserve theme and other non-auth settings
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('onboardingProgress');
    localStorage.removeItem('searchExecutionState');
    // Keep 'theme' in localStorage
    setUser(null);
    setError(null);
    window.location.replace('/');
  };

  const updateUser = (userData) => {
    setUser(prevUser => {
      const updatedUser = { ...prevUser, ...userData };
      // Also update localStorage to persist the changes
      localStorage.setItem('user', JSON.stringify(updatedUser));
      // Sync language if user has locked language preference
      syncLanguageWithUser(updatedUser);
      return updatedUser;
    });
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateUser,
    clearError,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;